import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useProduct } from "@/hooks/useProducts";
import { useCart } from "@/contexts/CartContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, ShoppingCart, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";

const ProductDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: product, isLoading } = useProduct(slug || "");
  const { addToCart: addToCartHandler } = useCart();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);

  const addToCart = () => {
    if (!product || product.stock_status !== "instock") {
      toast.error("Produsul nu este disponibil în stoc");
      return;
    }

    const cartItem = {
      id: product.id,
      wordpress_id: product.wordpress_id,
      name: product.name,
      price: product.sale_price && product.sale_price < product.regular_price!
        ? product.sale_price
        : product.price,
      image: images[0]?.src || "https://images.unsplash.com/photo-1543002588-bfa74002ed7e?q=80&w=600",
      slug: product.slug,
    };

    addToCartHandler(cartItem, quantity);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-grow pt-20">
          <div className="container mx-auto px-4 py-8">
            <Skeleton className="h-8 w-32 mb-8" />
            <div className="grid md:grid-cols-2 gap-12">
              <Skeleton className="h-96 w-full" />
              <div className="space-y-4">
                <Skeleton className="h-12 w-3/4" />
                <Skeleton className="h-8 w-24" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-grow pt-20">
          <div className="container mx-auto px-4 py-16 text-center">
            <h1 className="text-4xl font-bold text-primary mb-4">
              Produsul nu a fost găsit
            </h1>
            <Button onClick={() => navigate("/magazin")}>
              Înapoi la magazin
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const images = product.product_images?.length > 0
    ? product.product_images.sort((a, b) => a.position - b.position)
    : [{ src: "https://images.unsplash.com/photo-1543002588-bfa74002ed7e?q=80&w=600", alt: product.name, position: 0, id: "default" }];

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <main className="flex-grow pt-20 bg-muted/30">
        <div className="container mx-auto px-4 py-8">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate("/magazin")}
            className="mb-8 animate-fade-in"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Înapoi la magazin
          </Button>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Image Gallery */}
            <div className="space-y-4 animate-fade-in">
              <div className="relative aspect-square rounded-lg overflow-hidden bg-muted shadow-2xl">
                <img
                  src={images[selectedImage]?.src}
                  alt={images[selectedImage]?.alt || product.name}
                  className="w-full h-full object-cover"
                />
                {product.sale_price && product.sale_price < product.regular_price! && (
                  <Badge className="absolute top-4 right-4 bg-red-500 text-lg px-4 py-2 animate-scale-in">
                    -{Math.round((1 - product.sale_price / product.regular_price!) * 100)}%
                  </Badge>
                )}
              </div>
              
              {/* Thumbnail Gallery */}
              {images.length > 1 && (
                <div className="grid grid-cols-4 gap-4">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all hover-scale ${
                        selectedImage === idx
                          ? "border-primary shadow-lg"
                          : "border-transparent hover:border-primary/50"
                      }`}
                    >
                      <img
                        src={img.src}
                        alt={img.alt || product.name}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-6 animate-fade-in" style={{ animationDelay: "100ms" }}>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  {product.name}
                </h1>
                {product.sku && (
                  <p className="text-sm text-muted-foreground">SKU: {product.sku}</p>
                )}
              </div>

              {/* Price */}
              <div className="flex items-center gap-4">
                {product.sale_price && product.sale_price < product.regular_price! ? (
                  <>
                    <p className="text-5xl font-bold text-primary">
                      {product.sale_price.toFixed(2)} RON
                    </p>
                    <p className="text-2xl text-muted-foreground line-through">
                      {product.regular_price?.toFixed(2)} RON
                    </p>
                  </>
                ) : (
                  <p className="text-5xl font-bold text-primary">
                    {product.price.toFixed(2)} RON
                  </p>
                )}
              </div>

              {/* Stock Status */}
              <div>
                {product.stock_status === "instock" ? (
                  <Badge variant="default" className="bg-green-500 text-lg px-4 py-2">
                    În stoc
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="text-lg px-4 py-2">Stoc epuizat</Badge>
                )}
              </div>

              {/* Short Description */}
              {product.short_description && (
                <div
                  className="text-lg text-muted-foreground leading-relaxed prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.short_description) }}
                />
              )}

              {/* Quantity Selector */}
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                <span className="font-semibold">Cantitate:</span>
                <div className="flex items-center gap-2 bg-background rounded-lg p-1 border">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="w-12 text-center font-bold text-lg">
                    {quantity}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10"
                    onClick={() => setQuantity(quantity + 1)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Add to Cart Button */}
              <Button
                size="lg"
                className="w-full text-xl py-8 shadow-lg hover-scale"
                disabled={product.stock_status !== "instock"}
                onClick={addToCart}
              >
                <ShoppingCart className="mr-2 h-6 w-6" />
                Adaugă în coș
              </Button>

              {/* Categories */}
              {product.product_categories && product.product_categories.length > 0 && (
                <div className="pt-6 border-t">
                  <p className="text-sm font-semibold text-muted-foreground mb-3">
                    Categorii:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {product.product_categories.map((pc: any) => (
                      <Badge
                        key={pc.category.id}
                        variant="outline"
                        className="cursor-pointer hover-scale text-sm px-3 py-1"
                        onClick={() => navigate(`/magazin?category=${pc.category.slug}`)}
                      >
                        {pc.category.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Trust Badges */}
              <div className="pt-6 border-t space-y-3">
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span>Garanție calitate 100%</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <span>Meșteșug artizanal autentic</span>
                </div>
              </div>
            </div>
          </div>

          {/* Full Description */}
          {product.description && (
            <div className="mt-20 max-w-4xl mx-auto animate-fade-in" style={{ animationDelay: "200ms" }}>
              <h2 className="text-3xl font-bold text-primary mb-6">Descriere detaliată</h2>
              <div
                className="prose prose-lg max-w-none text-muted-foreground"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.description) }}
              />
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetail;
